import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CORE_LINE } from "@/content/faq";
import { PARENT_GUIDE } from "@/content/forParents";
import childPhoto from "@/assets/landing/hero-girl-768.webp.asset.json";

export const Route = createFileRoute("/for-parents")({
  head: () => ({
    meta: [
      { title: CORE_LINE },
      { name: "description", content: CORE_LINE },
      { property: "og:title", content: CORE_LINE },
      { property: "og:description", content: CORE_LINE },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://leadxkids.com/for-parents" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: CORE_LINE },
      { name: "twitter:description", content: CORE_LINE },
    ],
    links: [{ rel: "canonical", href: "https://leadxkids.com/for-parents" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": "https://leadxkids.com/for-parents#questions",
        mainEntity: PARENT_GUIDE.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.paragraphs.join("\n\n") },
        })),
      }),
    }],
  }),
  component: ForParentsPage,
});

function ForParentsPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <Header />
      <main id="main-content">
        <header className="border-b border-border bg-primary/5">
          <div className="mx-auto max-w-6xl px-5 py-9 sm:px-8 sm:py-12">
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 lx-body-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">Home</Link>
              <span aria-hidden="true">/</span>
              <span>For parents</span>
            </nav>
            <p className="mb-3 lx-caption font-semibold text-primary">A parent’s guide · Ages 8–14</p>
            <h1 className="lx-h1 mb-4">LeadXkids for Parents</h1>
            <p className="lx-body-lg max-w-3xl leading-relaxed" data-speakable>{CORE_LINE}</p>
            <p className="lx-body mt-4 max-w-2xl text-muted-foreground leading-relaxed">
              Confidence, big feelings, friendships and everyday choices. Thoughtful answers to the questions families ask before getting started.
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8 lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-12">
          <aside className="mb-10 lg:mb-0">
            <nav aria-label="Questions in this guide" className="lg:sticky lg:top-24">
              <p className="lx-h4 mb-4">Your questions, answered</p>
              <ol className="grid gap-1 sm:grid-cols-2 lg:grid-cols-1">
                {PARENT_GUIDE.map((item, index) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="flex gap-3 rounded-md px-2 py-2.5 lx-body-sm text-muted-foreground hover:bg-primary/5 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <span className="w-5 shrink-0 text-primary tabular-nums">{String(index + 1).padStart(2, "0")}</span>
                      <span>{item.q}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <article className="min-w-0">
            <figure className="mb-8 flex items-center gap-5 border-y border-border py-5">
              <img src={childPhoto.url} alt="A smiling Indian child" width={768} height={768} loading="lazy" className="h-28 w-28 shrink-0 rounded-lg object-cover sm:h-36 sm:w-36" />
              <figcaption className="min-w-0">
                <p className="lx-h3 mb-2">Start with understanding.</p>
                <p className="lx-body text-muted-foreground leading-relaxed">Not labels. Not comparisons. One everyday situation, one small step at a time.</p>
              </figcaption>
            </figure>
            {PARENT_GUIDE.map((item, index) => (
              <section key={item.id} id={item.id} className="scroll-mt-24 border-b border-border py-7 first:pt-0">
                <div className="mb-4 flex items-start gap-3">
                  <span className="lx-body-sm mt-1 shrink-0 font-semibold text-secondary tabular-nums">{String(index + 1).padStart(2, "0")}</span>
                  <h2 className="lx-h3">{item.q}</h2>
                </div>
                <div className="space-y-4">
                  {item.paragraphs.map((paragraph, i) => (
                    <p key={i} data-speakable className={`lx-body leading-relaxed ${i === 0 ? "text-foreground" : "text-muted-foreground"}`}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
            <section className="py-9">
              <h2 className="lx-h2 mb-3">Find a skill that fits your child.</h2>
              <p className="lx-body mb-5 text-muted-foreground">Explore the course topics together, without pressure to do everything at once.</p>
              <Button asChild><Link to="/courses">Explore all courses <ArrowRight aria-hidden="true" /></Link></Button>
              <nav aria-label="Related parent resources" className="mt-8 flex flex-wrap gap-x-6 gap-y-3 lx-body-sm">
                <Link to="/faq" className="inline-flex items-center gap-1 text-primary underline underline-offset-4">Short FAQ <ArrowUpRight className="size-4" aria-hidden="true" /></Link>
                <Link to="/about" className="text-primary underline underline-offset-4">About the founder</Link>
                <Link to="/personality-assessment" className="text-primary underline underline-offset-4">Know Your Child & sample report</Link>
                <Link to="/subscription" className="text-primary underline underline-offset-4">Membership terms & prices</Link>
              </nav>
            </section>
          </article>
        </div>
        <footer className="border-t border-border px-5 py-6">
          <div className="mx-auto flex max-w-6xl flex-wrap justify-between gap-4 lx-body-sm text-muted-foreground">
            <p>LeadXkids · Built in Bengaluru for children aged 8–14</p>
            <a href="mailto:hello@leadxkids.com" className="text-primary">hello@leadxkids.com</a>
          </div>
        </footer>
      </main>
    </div>
  );
}